import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { useForm } from 'react-hook-form';

import GooglePlaces from '../Additional/GooglePlaces';
import Navigation from '../Overlays/Navigation';
import PageTitle from 'components/Additional/PageElements/PageTitle/PageTitle';
import CustomSelect from 'components/Additional/CustomSelect/CustomSelect';
import Footer from 'components/Additional/Footer/Footer';
import useToast from 'components/Additional/Toast/useToast';
import CustomCheckbox from 'components/Additional/CustomCheckbox/CustomCheckbox';
import CustomInput from 'components/Additional/CustomInput/CustomInput';
import DidYouKnow from 'components/Additional/DidYouKnow/DidYouKnow';
import ManualAddressForm from 'components/Page 1/ManualAddressForm';

import {
	verifyAddress,
	createQuote,
	updateQuote,
	createQuoteDB,
	createActivityQuoteDB,
} from '../API/Model';

import './Home_page.css';
import 'components/styles/styles.css';
import NextButton from 'components/Additional/NextButton/NextButton';

import {
	getPolicyType,
	getBusinessLine,
	getUsageType,
	getHO6OccupancyType,
	saveProductVersion,
	getPolicyEffectiveDate,
	getPolicyExpirationDate,
	quoteTypeMapper,
	formatDateForAPI,
} from 'tools/CreateQuoteHelper';

import { saveQuoteToCreateData } from 'tools/LocalStorageHelper';

const Home_page = () => {
	const history = useHistory();

	const { showToast } = useToast();

	const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [quoteStatus, setQuoteStatus] = useState(null);
	const [addressInfo, setAddressInfo] = useState(null);
	const [isAddressInfoCorrect, setIsAddressInfoCorrect] = useState(false);
	const [isManualAddressSelected, setIsManualAddressSelected] =
		useState(false);
	const [isLatLongNeeded, setIsLatLongNeeded] = useState(false);
	const [latLongInfoRequest, setLatLongInfoRequest] = useState(null);

	const [manualAddressFormData, setManualAddressFormData] = useState({
		addr1: '',
		city: '',
		state: 'Florida',
		postalCode: '',
		latitude: '',
		longtitude: '',
	});

	const handleManualAddressChange = (e) => {
		const { name, value } = e.target;
		setManualAddressFormData((prev) => {
			const updatedState = {
				...prev,
				[name]: value,
			};

			localStorage.setItem(
				'personal_information',
				JSON.stringify({
					main_addr_street: updatedState.addr1,
					main_addr_city: updatedState.city,
					main_addr_zip: updatedState.postalCode,
					main_addr_state_code: 'FL',
					main_addr_state: updatedState.state,
				})
			);

			return updatedState;
		});
	};

	const [ownerType, setOwnerType] = useState('0');
	const [occupancyType, setOccupancyType] = useState('0');
	const [rentalExposure, setRentalExposure] = useState('0');

	const ownerTypeOptions = [
		{ name: 'Homeowner', value: 'OwnerSingleFamilyHome' },
		{ name: 'Condominium Owner', value: 'OwnerCondo' },
		{ name: 'Renter', value: 'Renter' },
		{ name: 'Landlord', value: 'Landlord' },
		{ name: 'Mobile Home Owner', value: 'OwnerMobileHome' },
	];

	const occupancyTypeOptions = [
		{ name: 'Primary Home', value: 'Owner Occupied' /*'1'*/ },
		{ name: 'Secondary Home', value: 'Immediate Family' /*'2'*/ },
		{ name: 'Rental Property', value: 'Tenant Occupied' /*'3'*/ },
		{ name: 'Vacant', value: 'Vacant' /*'4'*/ },
	];

	const rentalExposureOptions = [
		{ name: 'No Rental Exposure', value: '1' },
		{ name: 'Rental Exposure', value: '2' },
	];

	const savePolicyType = (e) =>
		localStorage.setItem('policy_type', getPolicyType(e));

	const {
		register,
		setValue,
		watch,
		formState: { errors },
		handleSubmit,
		clearErrors,
	} = useForm({
		shouldUnregister: false,
		mode: 'onChange',
	});

	const formatCurrency = (num) => {
		return num
			? '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0 })
			: '';
	};

	const onOwnerTypeChange = (e) => {
		const { name, value } = e.target;
		setOwnerType(value);
		setValue('cova', '');
		clearErrors('cova');
		localStorage.setItem('owner_type', value);
	};

	const onOccupancyTypeChange = (e) => {
		const { name, value } = e.target;
		setOccupancyType(value);
		setValue('cova', '');
		clearErrors('cova');
		localStorage.setItem('occupancy_type', value);
	};

	const onRentalExposureChange = (e) => {
		const { name, value } = e.target;
		setRentalExposure(value);
		localStorage.setItem('rental_exposure', value);
	};

	const handleCreateQuote = async (cova) => {
		let localQuoteStatus = null;
		setLoading(true);
		if (latLongInfoRequest) {
			const dtoBuilding =
				latLongInfoRequest.DTOApplication[0].DTOLine[0].DTORisk[0]
					.DTOBuilding[0];
			dtoBuilding.AddrVerifiedInd = 'Manual';
			dtoBuilding.Latitude = manualAddressFormData.latitude;
			dtoBuilding.Longitude = manualAddressFormData.longtitude;
			const stringPayload = JSON.stringify(latLongInfoRequest);
			const updateQuoteResponse = await updateQuote(stringPayload);
			setLatLongInfoRequest(null);
			if (updateQuoteResponse === 'SUCCESS') {
				setLoading(false);
				history.push('/PropertyInfo');
			}
			return;
		}
		let personalInfo = JSON.parse(
			window.localStorage.getItem('personal_information')
		);
		const appConfig = window.APP_CONFIG;
		personalInfo.first_name = appConfig.STATIC_DATA.FirstName;
		personalInfo.last_name = appConfig.STATIC_DATA.LastName;
		personalInfo.email = appConfig.STATIC_DATA.Email;
		personalInfo.phone = appConfig.STATIC_DATA.Phone;
		personalInfo.birth = appConfig.STATIC_DATA.Birth;

		window.localStorage.setItem(
			'personal_information',
			JSON.stringify(personalInfo)
		);

		const quoteType = localStorage.getItem('policy_type');

		const createAddressItem = (addressType) => {
			return isManualAddressSelected
				? {
						AddrTypeCd: addressType,
						Addr1: manualAddressFormData.addr1,
						PostalCode: manualAddressFormData.postalCode,
						City: manualAddressFormData.city,
						StateProvCd: 'FL',
				  }
				: {
						AddrTypeCd: addressType,
						Addr1:
							addressInfo.street +
							' ' +
							addressInfo.street_number,
						PostalCode: addressInfo.zip,
						City: addressInfo.city,
						StateProvCd: 'FL',
				  };
		};

		let quote_DTOInsured = [
			{
				EntityTypeCd: 'Individual',
				PreferredDeliveryMethod: 'Email',
				PartyInfo: [
					{
						PartyTypeCd: 'InsuredParty',
						Status: 'Active',
						PersonInfo: [
							{
								BestWayToContact: 'Email',
								BirthDt: formatDateForAPI(personalInfo.birth),
								PersonTypeCd: 'InsuredPersonal',
							},
						],
						NameInfo: [
							{
								NameTypeCd: 'InsuredName',
								GivenName: personalInfo.first_name,
								OtherGivenName: '',
								Surname: personalInfo.last_name,
								CommercialName:
									personalInfo.first_name +
									' ' +
									personalInfo.last_name,
							},
						],
						EmailInfo: {
							EmailTypeCd: 'InsuredEmail',
							EmailAddr: personalInfo.email,
							PreferredInd: 'No',
						},
						PhoneInfo: {
							PhoneTypeCd: 'InsuredPhonePrimary',
							PhoneNumber: personalInfo.phone,
							PreferredInd: 'No',
							PhoneName: 'Mobile',
						},
						Addr: [
							createAddressItem('InsuredMailingAddr'),
							createAddressItem('InsuredBillingAddr'),
							createAddressItem('InsuredLookupAddr'),
							createAddressItem('InsuredRiskAddr'),
						],
					},
					null,
				],
				IndexName:
					personalInfo.first_name + ' ' + personalInfo.lirst_name,
			},
		];

		let quote = {
			ExternalValidateBeanExist: 'No',
			ByPassCustomerCreation: 'No',
			DTOApplication: [
				{
					Status: 'In Process',
					TypeCd: 'Quote',
					CustomerRef: '',
					DTOTransactionInfo: {
						TransactionCd: 'New Business',
						SourceCd: 'Innovation',
						TransactionShortDescription: 'New Business',
						TransactionLongDescription: 'New Business',
						TransactionEffectiveDt: '20220411',
						TransactionNumber: '0',
					},
					DTOLine: [
						{
							StatusCd: 'Active',
							LineCd: getBusinessLine(quoteType),
							RatingService: 'ManualRate',
							DTORisk: [
								{
									TypeCd: getBusinessLine(quoteType),
									DTOBuilding: [
										{
											CovALimit: quoteType !== "HO4"
												? cova
												: null,
											CovCLimit: quoteType === "HO4"
												? cova
												: null,
											WaterDamageCov: "Standard",
											OccupancyType: quoteType === 'HO6'
												? getHO6OccupancyType(occupancyType)
												: occupancyType,
											UsageType: getUsageType(
												quoteType,
												occupancyType,
												rentalExposure
											),
											RentalTerm:
												quoteType === 'DF1'
													? 'Annual'
													: null,
											Addr: [
												createAddressItem(
													'RiskLookupAddr'
												),
											],
										},
									],
								},
							],
						},
					],
					QuestionReplies: {
						QuestionReply: [
							{
								Name: 'Losses',
								Value: '',
							},
							{
								Name: 'Foreclosure',
								Value: '',
							},
							{
								Name: 'Declined',
								Value: '',
							},
							{
								Name: 'Damage',
								Value: '',
							},
							{
								Name: 'Construction',
								Value: '',
							},
							{
								Name: 'MultipleStructures',
								Value: '',
							},
							{
								Name: 'Vicious',
								Value: '',
							},
							{
								Name: 'Sinkhole',
								Value: '',
							},
							{
								Name: 'Crime',
								Value: '',
							},
							{
								Name: 'HomeDayCare',
								Value: '',
							},
							{
								Name: 'Business',
								Value: '',
							},
							{
								Name: 'Lawsuit',
								Value: '',
							},
							{
								Name: 'Sale',
								Value: '',
							},
							{
								Name: 'Occupied',
								Value: '',
							},
							{
								Name: 'CertifyUnderwritingQA',
								Value: '',
							},
							{
								Name: 'Misrepresented',
								Value: '',
							},
						],
					},
					DTOInsured: quote_DTOInsured,
					DTOBasicPolicy: [
						{
							EffectiveDt: getPolicyEffectiveDate(),
							ExpirationDt: getPolicyExpirationDate(),
							PolicyVersion: '1',
							RenewalTermCd: '1 Year',
							SubTypeCd: localStorage.getItem('policy_type'),
							PolicySource: 'New Business Application',
							TransactionNumber: '1',
							Description: getBusinessLine(quoteType),
							ProviderNumber:
								localStorage.getItem('producer_code'),
							ControllingStateCd: 'FL',
							ProductVersionIdRef:
								localStorage.getItem('product_version'),
							CarrierGroupCd: 'SFI',
							BusinessSourceCd:
								localStorage.getItem('business_source'),
							TransactionCd: 'New Business',
						},
					],
				},
			],
		};
		async function fetchData() {
			const personalInfo = JSON.parse(
				localStorage.getItem('personal_information')
			);
			const verifiedAddressResponse = await verifyAddress(
				JSON.stringify({
					addr1: personalInfo.main_addr_street,
					city: personalInfo.main_addr_city,
					stateProvCd: personalInfo.main_addr_state_code,
					county: personalInfo.main_addr_county,
					postalCode: personalInfo.main_addr_zip,
				})
			);
			let verificationMessage;
			if (!verifiedAddressResponse.success) {
				if (
					verifiedAddressResponse.error ===
						'Service Error: The input address did not match the ZIP+4 record.' &&
					isManualAddressSelected
				) {
					verificationMessage =
						'Could not match address:&#xA;Service Error: The input address did not match the ZIP+4 record.';
				} else {
					localQuoteStatus = {
						status: 'ERROR',
						message:
							'Your address could not be found. Please enter your address manually to obtain an estimate.',
					};
					return;
				}
			}
			const { id, ...restVerifiedAddress } =
				verifiedAddressResponse.data.addresses[0];
			const updatedInsuredAddresses =
				quote_DTOInsured[0].PartyInfo[0].Addr.map((item) => ({
					...restVerifiedAddress,
					addrTypeCd: item.AddrTypeCd,
				}));
			const updatedRiskAddr =
				quote.DTOApplication[0].DTOLine[0].DTORisk[0].DTOBuilding[0].Addr.map(
					(item) =>
						item.AddrTypeCd === 'RiskLookupAddr'
							? {
									...restVerifiedAddress,
									addrTypeCd: item.AddrTypeCd,
							  }
							: item
				);

			quote_DTOInsured[0].PartyInfo[0].Addr = updatedInsuredAddresses;
			quote.DTOApplication[0].DTOLine[0].DTORisk[0].DTOBuilding[0].Addr =
				updatedRiskAddr;

			const response = await createQuote(JSON.stringify(quote));
			saveQuoteToCreateData(quote);

			setQuoteStatus(response.status);
			localQuoteStatus = {
				status: response.status,
				message: response.message,
			};
			if (
				response.message !==
				'Service temporarily unavailable. Please try again later.'
			) {
				databaseUpdate();
			}

			createActivityQuoteDB(
				'Index',
				'API_RES',
				'Create quote response - ' + response.status
			);

			if (!verificationMessage) return;

			const dtoBuilding =
				response.data.DTOApplication[0].DTOLine[0].DTORisk[0]
					.DTOBuilding[0];
			if (dtoBuilding.AddrVerifiedInd !== 'Yes') {
				setIsLatLongNeeded(true);
				setLatLongInfoRequest({
					ExternalValidateBeanExist: 'No',
					DTOApplication: response.data.DTOApplication,
				});
				localQuoteStatus = { status: 'latlong' };
			}
		}
		createActivityQuoteDB('Index', 'API_REQ', 'Create quote request');
		await fetchData();
		setLoading(false);
		if (localQuoteStatus.status === 'latlong') {
			return;
		} else if (localQuoteStatus.status === 'SUCCESS') {
			history.push('/PropertyInfo');
		} else if (localQuoteStatus.message != null) {
			showToast(localQuoteStatus.message, 'Error');
			setQuoteStatus(null);
		}
	};

	async function databaseUpdate() {
		const appConfig = window.APP_CONFIG;
		const addr1 = isManualAddressSelected
			? manualAddressFormData.addr1
			: addressInfo.street + ' ' + addressInfo.street_number;
		const zip = isManualAddressSelected
			? manualAddressFormData.postalCode
			: addressInfo.zip;
		const payload = {
			Quote_Number: localStorage.getItem('quote_number'),
			Email: appConfig.STATIC_DATA.Email,
			Insured_First_Name: appConfig.STATIC_DATA.FirstName,
			Insured_Last_Name: appConfig.STATIC_DATA.LastName,
			CoApplicant_First_Name: '',
			CoApplicant_Last_Name: '',
			Address: addr1,
			Zip_code: zip,
			Quote_Status_code: 'STR',
			Session_id: localStorage.getItem('session_id'),
		};
		localStorage.setItem('create-quote-db', JSON.stringify(payload));
		await createQuoteDB(payload);

		await fetch(
			`${window.APP_CONFIG.INTERNAL_API_URL}/portal/createQuoteInformation`,
			{
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					Quote_Number: localStorage.getItem('quote_number'),
				}),
			}
		);

		createActivityQuoteDB('IndexPage', 'NAV', 'Continue button');
	}

	const onSubmit = async (data) => {
		if (ownerType === 'OwnerMobileHome') {
			history.push('/QuickQuoteReferal');
			return;
		}

		savePolicyType(data.cova);
		saveProductVersion(data.cova);

		createActivityQuoteDB('IndexPage', 'NAV', 'Conitnue button');
		//window.grecaptcha.enterprise.ready(async function () {
		//	try {
		//		const token = await window.grecaptcha.enterprise.execute(
		//			'6LfCsp0dAAAAAK_ovmpumPsEqegcHoMmNcxz89u3',
		//			{ action: 'LOGIN' }
		//		);
		//	} catch (e) {
		//		console.error('Capcha Error : ', e);
		//	}
		//});

		// calculate applicable forms in order by importance
		const applicableQuoteTypes = quoteTypeMapper[ownerType];
		// use applicableQuoteTypes[0] to create quote
		localStorage.setItem(
			'quoteTypes',
			JSON.stringify(applicableQuoteTypes)
		);
		// create quote for the first tipe in array by template
		await handleCreateQuote(data.cova);
	};

	useEffect(() => {
		setLatLongInfoRequest(null);
	}, [isManualAddressSelected]);

	useEffect(() => {
		if (isManualAddressSelected) return;

		if (!addressInfo) {
			setIsAddressInfoCorrect(false);
			setValue('cova', '');
			return;
		}

		if (addressInfo.state !== 'Florida') {
			setIsAddressInfoCorrect(false);
			showToast(
				'We only offer policies for homes in Florida. Thanks for checking us out.',
				'Error',
				null
			);
		} else {
			if (
				addressInfo.city &&
				addressInfo.country &&
				addressInfo.state &&
				addressInfo.street &&
				addressInfo.street_number &&
				addressInfo.zip
			) {
				setIsAddressInfoCorrect(true);
				showToast('', 'Error', 1);
			} else {
				setIsAddressInfoCorrect(false);
				showToast(
					'We are unable to complete your estimate at this time. Please contact customer service at ' +
						window.APP_CONFIG.CONTACT_PHONE +
						'.',
					'Error'
				);
			}
		}
	}, [addressInfo]);

	useEffect(() => {
		if (!isManualAddressSelected) return;

		const filled =
			!!manualAddressFormData.addr1 &&
			!!manualAddressFormData.city &&
			!!manualAddressFormData.postalCode &&
			(isLatLongNeeded
				? !!manualAddressFormData.latitude &&
				  !!manualAddressFormData.longtitude
				: true);
		setIsAddressInfoCorrect(filled);
	}, [manualAddressFormData]);

	useEffect(() => {
		const fetchData = async () => {
			localStorage.setItem('status', 'STR');
			localStorage.setItem('session_id', uuidv4());
			localStorage.setItem('current_page', 'Home');
			const res = await axios.get('https://geolocation-db.com/json/');
			localStorage.setItem('user_ip', res.data.IPv4);
			createActivityQuoteDB('IndexPage', 'PL', 'Index page loaded');
		};

		localStorage.removeItem('customer_ref');
		localStorage.removeItem('updatedUserDataPdfUrl');
		localStorage.removeItem('personal_information');
		localStorage.removeItem('quote_number');
		localStorage.removeItem('quote_to_send');
		localStorage.removeItem('quote_information');
		localStorage.removeItem('hazard_hub_data');
		localStorage.removeItem('hazard_hub_data_used');
		localStorage.removeItem('owner_type');
		localStorage.removeItem('occupancy_type');
		localStorage.removeItem('rental_exposure');
		localStorage.removeItem('policy_type');
		localStorage.removeItem('discounts_data');
		localStorage.removeItem('quoteTypes');
		localStorage.removeItem('applicableQuoteTypes');
		localStorage.removeItem('created_quotes_numbers');
		localStorage.removeItem('quote_to_create_data');
		localStorage.removeItem('create-quote-db');

		fetchData();
	}, []);

	useEffect(() => {
		if (!mobileTooltipOpen) return;

		const handleClickOutside = (e) => {
			const target = e.target;
			const tooltip = document.querySelector(
				'.baner_icon_info_home.mobile-tooltip-active'
			);
			if (tooltip && !tooltip.contains(target)) {
				setMobileTooltipOpen(false);
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [mobileTooltipOpen]);

	const covAValue = watch('cova');
	register('cova', {
		validate: (value) => {
			const ownerType = localStorage.getItem('owner_type');

			if (value === '' && ownerType !== 'OwnerMobileHome')
				return 'Field is required';

			if (
				ownerType === 'OwnerCondo' &&
				(value < 30000 || value > 500000)
			) {
				//HO6
				return 'Value should be in range 30,000 - 500,000';
			} else if (
				ownerType === 'Renter' &&
				(value < 15000 || value > 100000)
			) {
				//HO4
				return 'Value should be in range 15,000 - 100,000';
			} else if (
				(ownerType === 'OwnerSingleFamilyHome' ||
					ownerType === 'Landlord') &&
				(value < 30000 || value > 5000000)
			) {
				return 'Value should be in range 30,000 - 5,000,000';
			}

			if (value > 5000000)
				return "Value shouldn't be bigger than 5,000,000";
			return true;
		},
	});

	return (
		<div className="base-wrapper">
			<div className="abs-wrapper">
				<Navigation page={0} progress={0} />
			</div>
			{loading ? (
				<DidYouKnow />
			) : (
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="home-page-form"
				>
					<div className="home-page-wrapper">
						<div className="width-wrapper">
							<PageTitle
								titleContent={<>Let's start your estimate</>}
							/>
							<div className="home-page-content-container">
								<div className="home-page-image"></div>
								<div className="home-page-content">
									<div className="short-width-block">
										<CustomSelect
											label="I am a"
											name="ownerType"
											layout="horizontal"
											options={ownerTypeOptions}
											customContainerClass="homeValue"
											onChange={onOwnerTypeChange}
											ref={register({
												validate: (value) =>
													value !== '0' ||
													'Field is required *',
											})}
											error={errors.ownerType?.message}
										/>
										{ownerType !== '0' &&
											ownerType !== 'OwnerMobileHome' &&
											ownerType !== 'Renter' && (
												<CustomSelect
													label="Occupancy Type"
													name="occupancyType"
													layout="horizontal"
													options={
														occupancyTypeOptions
													}
													customContainerClass="homeValue"
													onChange={
														onOccupancyTypeChange
													}
													ref={register({
														validate: (value) =>
															value !== '0' ||
															'Field is required *',
													})}
													error={
														errors.occupancyType
															?.message
													}
												/>
											)}
										{occupancyType === 'Immediate Family' &&
											ownerType !== 'OwnerMobileHome' && (
												<CustomSelect
													label="Rental Exposure"
													name="rentalExposure"
													layout="horizontal"
													options={
														rentalExposureOptions
													}
													customContainerClass="homeValue"
													onChange={
														onRentalExposureChange
													}
													ref={register({
														validate: (value) =>
															value !== '0' ||
															'Field is required *',
													})}
													error={
														errors.rentalExposure
															?.message
													}
												/>
											)}
									</div>
									{ownerType !== '0' &&
										ownerType !== 'OwnerMobileHome' &&
										(occupancyType !== '0' ||
											ownerType === 'Renter') && (
											<>
												<div className="row-container">
													<span className="label-text">
														Address not populating?
													</span>
													<CustomCheckbox
														id="alarmDiscount"
														value={
															isManualAddressSelected
														}
														onChange={() =>
															setIsManualAddressSelected(
																(prev) => !prev
															)
														}
													/>
												</div>
												{isManualAddressSelected ? (
													<ManualAddressForm
														formData={
															manualAddressFormData
														}
														onChange={
															handleManualAddressChange
														}
														latlong={
															isLatLongNeeded
														}
													/>
												) : (
													<GooglePlaces
														setAdressInfo={
															setAddressInfo
														}
													/>
												)}

												<div className="covaInputWrapper">
													<div
														className={`homeValue covaInput ${
															!!errors.cova
																? 'error'
																: ''
														}`}
													>
														<div className="covaWrapper">
															<CustomInput
																inputName="cova"
																placeholder={
																	ownerType ===
																		'OwnerSingleFamilyHome' ||
																	ownerType ===
																		'Landlord'
																		? 'Your home value'
																		: 'Dwelling Coverage'
																}
																value={
																	covAValue
																		? formatCurrency(
																				covAValue
																		  )
																		: ''
																}
																onChange={(
																	e
																) => {
																	const targetValue =
																		e.target.value.replace(
																			/[$,]/g,
																			''
																		);
																	setValue(
																		'cova',
																		targetValue ===
																			''
																			? ''
																			: Number(
																					targetValue
																			  ),
																		{
																			shouldValidate: true,
																		}
																	);
																}}
																error={
																	errors.cova
																		?.message ??
																	''
																}
															/>
														</div>
														<div
															className={`baner_icon_info_home baner_icon ${
																mobileTooltipOpen
																	? 'mobile-tooltip-active'
																	: ''
															}`}
															data-tooltip={
																ownerType ===
																	'OwnerSingleFamilyHome' ||
																ownerType ===
																	'Landlord'
																	? 'This is how much it would cost to replace your home.  This information can be found on your current Homeowners Declaration page as Coverage A. For new purchases, refer to the appraised value of the structure excluding land value.'
																	: ownerType ===
																		  'OwnerCondo'
																		? 'This is how much it would cost to replace the interior structure of a condo unit, typically covering everything from the "walls in" that the owner is responsible for, such as flooring, drywall, cabinets, countertops, lighting fixtures, and any additional modifications made to the unit.'
																		: 'The estimated value of the personal property within your residence (such as furniture, television sets, clothing, etc.)'
															}
															onClick={(e) => {
																if (
																	window.innerWidth <=
																	768
																) {
																	e.stopPropagation();
																	setMobileTooltipOpen(
																		(
																			prev
																		) =>
																			!prev
																	);
																}
															}}
														/>
													</div>
												</div>
											</>
										)}
									<div className="continue-button-wrapper">
										<NextButton
											customClass="mx-auto"
											text="Continue"
											type="submit"
											isDisabled={
												ownerType !==
													'OwnerMobileHome' &&
												(!isAddressInfoCorrect ||
													!covAValue ||
													ownerType === '0')
											}
										/>
									</div>
									<div className="homeValue">
										<p className="privacy-text">
											Your privacy is our priority.
											Requesting an insurance quote will
											not result in unsolicited calls,
											emails, or texts.
										</p>
									</div>
								</div>
							</div>
						</div>
						<Footer />
					</div>
				</form>
			)}
		</div>
	);
};

export default Home_page;
